import DynamicDialog, { type ButtonVariant, type DialogProps, type DialogResult } from '~/components/DynamicDialog.vue'
import { createVNode, render, getCurrentInstance, type VNode } from 'vue'
import { ConfigProvider } from 'reka-ui'

type AlertDialogOptions = {
  title?: string | VNode
  description?: string | VNode
  body?: string | VNode
  confirmButtonText?: string
  confirmButtonVariant?: ButtonVariant
  defaultFocusButton?: 'cancel' | 'confirm'
  confirmButtonClass?: string
  cancelButtonClass?: string
  dialogContentClass?: string
}

type ConfirmDialogOptions = AlertDialogOptions & {
  cancelButtonText?: string
  cancelButtonVariant?: ButtonVariant
}

type CustomDialogOptions = {
  component: any // Vue component
  componentProps?: Record<string, any>
  componentClass?: any
  componentWrapperClass?: any
  title?: string | VNode
  description?: string | VNode
  hideButtons?: boolean
  dialogContentClass?: string
}

const currentDialogs = new Set<VNode>()
let nextDialogId = 0

const showDialog = (options: Omit<DialogProps, 'onClose'>): Promise<DialogResult> => {
  if (!useIsBrowser()) return Promise.resolve('close')

  return new Promise((resolve) => {
    const nuxtApp = useNuxtApp()
    const container = document.createElement('div')
    const vnode = createVNode(DynamicDialog, {
      ...options,
      onClose: (result: DialogResult) => {
        currentDialogs.delete(vnode)
        resolve(result)
        // Unmount releases Reka's focus and scroll locks, including stacked dialogs.
        nextTick(() => {
          render(null, container)
          container.remove()
        })
      },
    })
    // Detached Vue roots have separate useId counters. Share an ID source so
    // stacked dialogs keep distinct labels and focus targets.
    const root = createVNode(ConfigProvider, { useId: () => `panda-dialog-${++nextDialogId}` }, () => vnode)
    root.appContext = getCurrentInstance()?.appContext || nuxtApp.vueApp._context
    currentDialogs.add(vnode)
    document.body.appendChild(container)
    try {
      nuxtApp.runWithContext(() => render(root, container))
    } catch (error) {
      currentDialogs.delete(vnode)
      render(null, container)
      container.remove()
      throw error
    }
  })
}

export const useDialog = () => ({
  alert: (options: AlertDialogOptions) => showDialog({ ...options, hideCancelButton: true }),
  confirm: (options: ConfirmDialogOptions) => showDialog({ ...options, hideCancelButton: false }),
  custom: (options: CustomDialogOptions) =>
    showDialog({
      ...options,
      hideButtons: options.hideButtons !== false,
      hideCancelButton: true,
    }),
  close: (vnode?: VNode) => {
    for (const dialog of vnode ? [vnode] : [...currentDialogs]) {
      if (currentDialogs.has(dialog)) dialog.component?.exposed?.close()
    }
  },
})
